import { Check, ChevronsUpDown, FolderPlus, Settings2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useProfilesContext } from "@/context/ProfileContext";
import { profileColor } from "@/lib/profileColor";
import { cn } from "@/lib/utils";

/** Small colored dot identifying a profile (deterministic color from its id). */
export function ProfileDot({ seed, className }: { seed: string; className?: string }): JSX.Element {
  return (
    <span
      className={cn("inline-block h-2.5 w-2.5 shrink-0 rounded-full", className)}
      style={{ backgroundColor: profileColor(seed) }}
      aria-hidden
    />
  );
}

export interface ProfileSwitcherProps {
  className?: string;
}

/** Active-profile switcher for the sidebar: shows the current profile, lists the rest. */
export function ProfileSwitcher({ className }: ProfileSwitcherProps): JSX.Element {
  const navigate = useNavigate();
  const { profiles, activeProfile, setActiveProfile } = useProfilesContext();
  const selectable = profiles.filter((profile) => !profile.archived);

  function choose(id: string): void {
    setActiveProfile(id);
    navigate("/dashboard");
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn("h-auto w-full justify-between px-3 py-2", className)}
          aria-label="Switch profile"
        >
          <span className="flex min-w-0 items-center gap-2">
            {activeProfile ? (
              <ProfileDot seed={activeProfile.id} />
            ) : (
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-dashed"
                aria-hidden
              />
            )}
            <span className="truncate text-sm font-medium">
              {activeProfile ? activeProfile.label : "No profile selected"}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        <DropdownMenuLabel>Profiles</DropdownMenuLabel>
        {selectable.length === 0 ? (
          <DropdownMenuItem disabled>No profiles yet</DropdownMenuItem>
        ) : (
          selectable.map((profile) => (
            <DropdownMenuItem key={profile.id} onSelect={() => choose(profile.id)}>
              <ProfileDot seed={profile.id} />
              <span className="truncate">{profile.label}</span>
              {activeProfile?.id === profile.id ? <Check className="ml-auto h-4 w-4" /> : null}
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/profiles?new=1")}>
          <FolderPlus className="h-4 w-4" />
          Create new profile
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate("/profiles")}>
          <Settings2 className="h-4 w-4" />
          Manage profiles
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
